import { TruncatedText } from '@noor/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import {
  entityText,
  ModerationOperationPill,
  ModerationStatusPill,
  payloadEntityOf,
  requestBaselineOf,
  requestCategoryName,
  requestDiscount,
  requestOperation,
  requestPromoType,
  sourceLabel,
  statusLabel,
  type ChangeRequest,
  type ModerationEntityType,
  type Translate,
} from '@/entities/moderation';
import { EntityCell } from './cells/entity-cell';
import { SubmittedCell } from './cells/submitted-cell';
import { VendorCell } from './cells/vendor-cell';

export type ModerationColumn = ColumnDef<ChangeRequest>;

const ENTITY_HEADERS = {
  products: ['table.product', 'Товар'],
  restaurants: ['table.restaurant', 'Ресторан'],
  categories: ['table.category', 'Категория'],
  promotions: ['table.promotion', 'Акция'],
} as const;

/**
 * Queue columns: entity cell, request kind, source,
 * queue-specific middle pair, submitted date, status pill — so what a row
 * asks and where it stands are both explicit before it is opened.
 */
export function useModerationColumns(
  entityType: ModerationEntityType,
  t: Translate,
): ModerationColumn[] {
  const [entityKey, entityFallback] = ENTITY_HEADERS[entityType];
  return useMemo(
    () => [
      {
        id: 'entity',
        header: t(entityKey, entityFallback),
        cell: ({ row: { original: request } }) => (
          <EntityCell entityType={entityType} request={request} t={t} />
        ),
      },
      {
        id: 'operation',
        header: t('table.operation', 'Тип заявки'),
        cell: ({ row: { original: request } }) => (
          <ModerationOperationPill operation={requestOperation(request)} t={t} />
        ),
      },
      {
        id: 'source',
        header: t('table.source', 'Источник'),
        cell: ({ row: { original: request } }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {request.source ? sourceLabel(request.source, t) : '—'}
          </span>
        ),
      },
      ...middleColumns(entityType, t),
      {
        id: 'submitted',
        header: t('table.submitted', 'Дата подачи'),
        cell: ({ row: { original: request } }) => <SubmittedCell request={request} />,
      },
      {
        id: 'status',
        header: t('table.status', 'Статус'),
        cell: ({ row: { original: request } }) => (
          <ModerationStatusPill
            status={request.status}
            label={statusLabel(request.status, t)}
          />
        ),
      },
    ],
    [entityType, entityKey, entityFallback, t],
  );
}

/**
 * The middle columns — what the reviewer scans — per queue. All values come
 * from the request's proposed `payload` (the queue's inline snapshot), never
 * from mock data: missing fields read as `—`.
 */
function middleColumns(
  entityType: ModerationEntityType,
  t: Translate,
): ModerationColumn[] {
  if (entityType === 'promotions') {
    return [
      {
        id: 'promoType',
        header: t('table.promoType', 'Тип'),
        cell: ({ row: { original: request } }) => <>{requestPromoType(request)}</>,
      },
      {
        id: 'discount',
        header: t('table.discount', 'Скидка'),
        cell: ({ row: { original: request } }) => (
          <span className="tabular-nums">{requestDiscount(request)}</span>
        ),
      },
    ];
  }
  const columns: ModerationColumn[] = [
    {
      id: 'vendor',
      header: t('table.vendor', 'Мерчант'),
      cell: ({ row: { original: request } }) => (
        <VendorCell merchantId={request.merchant_id} />
      ),
    },
  ];
  // Categories carry only a branch id as the second signal — raw ids stay
  // out of the table, so the second column is dropped there.
  if (entityType !== 'categories') {
    columns.push(secondMiddleColumn(entityType, t));
  }
  return columns;
}

function secondMiddleColumn(
  entityType: ModerationEntityType,
  t: Translate,
): ModerationColumn {
  switch (entityType) {
    case 'restaurants':
      return {
        id: 'phone',
        header: t('table.phone', 'Телефон'),
        cell: ({ row: { original: request } }) => (
          <span className="tabular-nums">
            {entityText(
              payloadEntityOf(request),
              'phone',
              entityText(requestBaselineOf(request) ?? {}, 'phone', '—'),
            )}
          </span>
        ),
      };
    default:
      return {
        id: 'category',
        header: t('table.category', 'Категория'),
        cell: ({ row: { original: request } }) => (
          <TruncatedText text={requestCategoryName(request)} className="block max-w-56" />
        ),
      };
  }
}
